import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Anchor, Bike, Tent, Mountain, Map, PartyPopper, Smartphone, Calendar, ShieldCheck, MessageSquare, CreditCard, DollarSign, MapPin, FileText, Bell, Building2, Rotate3D } from "lucide-react";
import { useState } from "react";
import AIChat from "@/components/AIChat";
import TrustStrip from "@/components/TrustStrip";
import CTABand from "@/components/CTABand";
import BoatClubTeaser from "@/components/boat-club/BoatClubTeaser";
import { useSeo } from "@/lib/seo";
import { useReveal } from "@/lib/reveal";

const FLEX_TABS = [
  {
    key: "pontoon",
    label: "Pontoon",
    icon: Anchor,
    dims: [
      { label: "Duration", value: "4h · 8h · full-day" },
      { label: "Capacity", value: "Up to 10 seats" },
      { label: "Price", value: "$340 · $560 · $890" },
      { label: "Add-ons", value: "Fuel, tubes, captain" },
    ],
  },
  {
    key: "kayak",
    label: "Kayak",
    icon: Map,
    dims: [
      { label: "Duration", value: "1h · 2h · half-day" },
      { label: "Capacity", value: "Single / tandem" },
      { label: "Price", value: "$22 · $38 · $65" },
      { label: "Add-ons", value: "Dry bag, PFD upgrade" },
    ],
  },
  {
    key: "equipment",
    label: "Equipment",
    icon: Mountain,
    dims: [
      { label: "Duration", value: "Day · Week · Month" },
      { label: "Capacity", value: "Per unit + deposit" },
      { label: "Price", value: "$185 · $640 · $1,950" },
      { label: "Add-ons", value: "Delivery, fuel, operator cert" },
    ],
  },
  {
    key: "tour",
    label: "Tour",
    icon: PartyPopper,
    dims: [
      { label: "Duration", value: "Time-slotted" },
      { label: "Capacity", value: "12 / sailing" },
      { label: "Price", value: "$85 per guest" },
      { label: "Add-ons", value: "Drinks, photos, transport" },
    ],
  },
];

const MOBILE_OPS = [
  { icon: MapPin, title: "GPS inventory", body: "Track every unit live — know what's out, where, and when it's due back." },
  { icon: FileText, title: "Photo check-in / out", body: "Damage-stamp every rental with timestamped photos pinned to the record." },
  { icon: MessageSquare, title: "SMS & email", body: "Confirmations, reminders, and follow-ups fire from the same number all season." },
  { icon: CreditCard, title: "Integrated POS", body: "Tap-to-pay on the dock. Add-ons at return. One reconciled ledger." },
  { icon: DollarSign, title: "On-the-fly pricing", body: "Raise rates on the holiday weekend in seconds — not a quarterly meeting." },
  { icon: Smartphone, title: "Runs from your phone", body: "Built for the operator who isn't behind a desk. Everything in one app." },
];

const ALL_IN_ONE = [
  { icon: ShieldCheck, title: "Built-in waivers", body: "Digital, dated, signed at checkout." },
  { icon: Calendar, title: "Seasonal calendars", body: "Multi-season schedules, weather blackouts." },
  { icon: Rotate3D, title: "Refunds & holds", body: "Partial refunds, deposits, credits." },
  { icon: Bell, title: "Auto reminders", body: "Renter shows up with the right gear." },
  { icon: Building2, title: "Multi-location", body: "Branches share inventory or run solo." },
  { icon: Sparkles, title: "AI booking assistant", body: "Renters self-serve. Owners convert more." },
];

const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "RentAnything.ai",
  url: "https://rentanything.ai",
  logo: "https://rentanything.ai/logo.svg",
  email: "inquiry@rentanything.ai",
  sameAs: ["https://www.linkedin.com/", "https://x.com/", "https://www.instagram.com/"],
  description: "Booking and operations software flexible enough to rent anything.",
};

export default function Home() {
  useSeo({
    title: "Booking software flexible enough to rent anything",
    description:
      "RentAnything.ai is AI-native booking and operations software for rental businesses across marine, equipment, recreation, powersports, tours and events.",
    path: "/",
    jsonLd: ORG_JSONLD,
  });
  useReveal();

  const [tab, setTab] = useState(FLEX_TABS[0].key);
  const active = FLEX_TABS.find((t) => t.key === tab) ?? FLEX_TABS[0];

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden ra-mesh">
        <div className="ra-container py-16 sm:py-24 lg:py-32">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr]">
            <div className="animate-fade-up">
              <span className="ra-pill" data-testid="hero-pill">
                <span className="h-1.5 w-1.5 rounded-full bg-ra-amber" />
                More than just a booking calendar
              </span>
              <h1 className="mt-5 ra-h1" data-testid="hero-headline">
                Booking software flexible enough to{" "}
                <span className="relative whitespace-nowrap">
                  <span className="relative z-10 text-ra-ocean">rent anything</span>
                  <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 200 8" fill="none" preserveAspectRatio="none">
                    <path d="M2 6 Q 50 1 100 5 T 198 4" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" fill="none" />
                  </svg>
                </span>
                .
              </h1>
              <p className="mt-5 ra-lead max-w-xl" data-testid="hero-sub">
                We model every rental as configurable dimensions — duration, capacity, location, pricing rules, add-ons — so one AI-native platform adapts to pontoons, kayaks, equipment, and whatever you rent next. No rebuild.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="ra-btn-primary" data-testid="hero-book-demo-btn">
                  Book a Demo <ArrowRight size={16} />
                </Link>
                <a
                  href="https://operator.rentanything.ai/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ra-btn-ghost"
                  data-testid="hero-operator-login-btn"
                >
                  Operator Login
                </a>
              </div>

              <div className="mt-10 flex items-center gap-6 text-xs text-ra-ink/55">
                <div className="flex items-center gap-2"><ShieldCheck size={14} className="text-ra-ocean" /> WCAG-AA, GDPR-ready</div>
                <div className="flex items-center gap-2"><Sparkles size={14} className="text-ra-amber" /> AI-native checkout</div>
              </div>
            </div>

            <div className="flex justify-center lg:justify-end">
              <AIChat />
            </div>
          </div>
        </div>
      </section>

      {/* FLEXIBILITY PROOF */}
      <section className="ra-section">
        <div className="ra-container">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.3fr] lg:items-end">
            <div className="reveal">
              <span className="ra-eyebrow">The dimensions engine</span>
              <h2 className="mt-3 ra-h2">Same platform. Wildly different rentals.</h2>
              <p className="mt-4 ra-lead">
                Toggle through how operators configure the exact same core for four different businesses. Nothing custom-coded.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 reveal">
              {FLEX_TABS.map((t) => {
                const Icon = t.icon;
                const isActive = tab === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    data-testid={`flex-tab-${t.key}`}
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                      isActive
                        ? "border-ra-ocean bg-ra-ocean text-white shadow-soft"
                        : "border-ra-line bg-white text-ra-ink/70 hover:border-ra-ocean/40 hover:text-ra-ink"
                    }`}
                  >
                    <Icon size={14} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 reveal">
            {active.dims.map((d) => (
              <div key={d.label} className="ra-card" data-testid={`flex-dim-${d.label.toLowerCase()}`}>
                <p className="ra-eyebrow !text-ra-ink/45 !text-[10px]">{d.label}</p>
                <p className="mt-3 font-display text-2xl font-semibold text-ra-ink">{d.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI BOOKING ASSISTANT */}
      <section className="ra-section bg-ra-paper border-y border-ra-line">
        <div className="ra-container grid gap-12 lg:grid-cols-2 lg:items-center">
          <div className="reveal">
            <span className="ra-eyebrow">AI booking assistant</span>
            <h2 className="mt-3 ra-h2">Renters self-serve. Owners convert more.</h2>
            <p className="mt-4 ra-lead">
              "I need a pontoon Saturday afternoon, seats 8." The assistant searches your live inventory, prices the slot, surfaces add-ons, and hands the booking back to your team — already paid for.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-ra-ink/80">
              {[
                "Pulls availability from your live calendar — never overbooks",
                "Handles waivers, payments, and add-on upsells in one flow",
                "Trained on your fleet, your rules, your tone",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-ra-ocean" /> {b}
                </li>
              ))}
            </ul>
            <Link to="/product" className="ra-btn-ghost mt-7" data-testid="ai-section-cta">
              See all AI features <ArrowRight size={14} />
            </Link>
          </div>
          <div className="flex justify-center lg:justify-end reveal">
            <AIChat />
          </div>
        </div>
      </section>

      {/* MOBILE OPS */}
      <section className="ra-section">
        <div className="ra-container">
          <div className="max-w-2xl reveal">
            <span className="ra-eyebrow">Mobile operations</span>
            <h2 className="mt-3 ra-h2">Run it from your phone, not a back-office binder.</h2>
            <p className="mt-4 ra-lead">Operators don't live behind a counter. Neither does the software.</p>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {MOBILE_OPS.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="ra-card reveal" data-testid={`mobile-op-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-ra-ocean/10 text-ra-ocean">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold text-ra-ink">{f.title}</h3>
                  <p className="mt-2 text-sm text-ra-ink/70 leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ALL IN ONE */}
      <section className="ra-section bg-ra-paper border-y border-ra-line">
        <div className="ra-container">
          <div className="max-w-2xl reveal">
            <span className="ra-eyebrow">Everything in one platform</span>
            <h2 className="mt-3 ra-h2">Waivers, calendars, refunds, reminders — already in the box.</h2>
            <p className="mt-4 ra-lead">No bolt-ons. No integrations to babysit. One product, one bill, one team that knows your fleet.</p>
          </div>
          <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {ALL_IN_ONE.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="flex items-start gap-4 rounded-2xl border border-ra-line bg-white p-5 reveal">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-ra-amber/15 text-ra-amberDeep">
                    <Icon size={18} />
                  </div>
                  <div>
                    <h3 className="font-display text-base font-semibold text-ra-ink">{f.title}</h3>
                    <p className="mt-1 text-sm text-ra-ink/70">{f.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BUILT BY OPERATORS */}
      <section className="ra-section">
        <div className="ra-container">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
            <div className="reveal">
              <span className="ra-eyebrow">Built by operators</span>
              <h2 className="mt-3 ra-h2">We run rental businesses. We just got tired of bad software.</h2>
              <p className="mt-5 ra-lead">
                Most booking tools were built by software people imagining what an operator's day looks like. We built RentAnything the other way around — on the dock, in the yard, fixing software that should already have worked.
              </p>
              <Link to="/about" className="ra-btn-ghost mt-7" data-testid="operators-cta">
                Meet the team <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 reveal">
              {[
                { k: "6", v: "Verticals supported" },
                { k: "100%", v: "Mobile-first" },
                { k: "30d", v: "Avg. onboarding" },
                { k: "24/7", v: "Operator support" },
              ].map((s) => (
                <div key={s.v} className="rounded-2xl border border-ra-line bg-white p-6 shadow-card">
                  <p className="font-display text-3xl font-semibold text-ra-ocean">{s.k}</p>
                  <p className="mt-1 text-sm text-ra-ink/65">{s.v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <BoatClubTeaser />
      <TrustStrip />
      <CTABand />
    </>
  );
}
